import React, { useState, useEffect } from "react";
import { Input, Select, Button, Table, InputNumber, message } from "antd";
import PropTypes from "prop-types";
import { assignAppraisal, updateAppraisal } from "../../../service/appraisalService";
import { getAllEmployeesMethod } from "../../../service/ApiServices";

const AssignAppraisal = ({ selectedAppraisal, handleCancel, handleReload, reviewPeriods, departments }) => {
  const [departmentId, setDepartmentId] = useState("");
  const [employeeIds, setEmployeeIds] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [kpis, setKpis] = useState([{ title: "", target: 0, weightage: 0, achieved: 0 }]);
  const [status, setStatus] = useState("Draft");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (departmentId) {
      const fetchEmployees = async () => {
        try {
          await getAllEmployeesMethod({
            setEmployeeData: (responseData) => {
              const employeeArray = responseData.data || [];
              setEmployeeIds(employeeArray.map(emp => emp._id));
            },
            setLoading,
            params: { page: 1, limit: 1000 },
            search: `departmentId=${departmentId}`,
          });
        } catch (error) {
          console.error("Error fetching employees:", error);
          setEmployeeIds([]);
          message.error("Failed to fetch employees for the selected department");
        }
      };
      fetchEmployees();
    } else {
      setEmployeeIds([]);
    }
  }, [departmentId]);

  useEffect(() => {
    if (selectedAppraisal) {
      setDepartmentId(selectedAppraisal.department_id?._id || "");
      setEmployeeIds([selectedAppraisal.employee_id?._id] || []);
      setPeriodId(selectedAppraisal.period_id?._id || "");
      setName(selectedAppraisal.name || "");
      setKpis(
        selectedAppraisal.kpis?.map(kpi => ({
          title: kpi.title,
          target: kpi.target,
          weightage: kpi.weightage,
          achieved: 0,
        })) || [{ title: "", target: 0, weightage: 0, achieved: 0 }]
      );
      setStatus(selectedAppraisal.status || "Draft");
    }
  }, [selectedAppraisal]);

  const handleKpiChange = (index, field, value) => {
    const newKpis = [...kpis];
    newKpis[index] = {
      ...newKpis[index],
      [field]: field === "target" || field === "weightage" ? Number(value) || 0 : value,
      achieved: 0,
    };
    setKpis(newKpis);
  };

  const addKpi = () => {
    setKpis([...kpis, { title: "", target: 0, weightage: 0, achieved: 0 }]);
  };

  const removeKpi = (index) => {
    if (kpis.length > 1) {
      setKpis(kpis.filter((_, i) => i !== index));
    } else {
      message.warning("At least one KPI is required.");
    }
  };

  const handleSubmit = async () => {
    const totalWeight = kpis.reduce((sum, kpi) => sum + Number(kpi.weightage || 0), 0);
    const invalidKpi = kpis.some(
      (kpi) => !kpi.title || kpi.target <= 0 || kpi.weightage <= 0
    );

    if (!name) {
      message.error("Please enter an appraisal name.");
      return;
    }

    if (!departmentId) {
      message.error("Please select a department.");
      return;
    }

    if (!periodId) {
      message.error("Please select a review period.");
      return;
    }

    if (invalidKpi) {
      message.error("All KPIs must have a title, a target greater than 0, and a weightage greater than 0.");
      return;
    }

    if (totalWeight !== 100) {
      message.error("Total KPI weightage must equal 100%.");
      return;
    }

    setLoading(true);
    try {
      const appraisalData = {
        name,
        departmentId,
        employeeIds: selectedAppraisal ? employeeIds : [],
        periodId,
        kpis: kpis.map(kpi => ({
          title: kpi.title,
          target: kpi.target,
          achieved: 0,
          weightage: kpi.weightage,
        })),
        behavioral_scores: {
          teamwork: 0,
          communication: 0,
          punctuality: 0,
        },
        status,
      };

      if (selectedAppraisal) {
        await updateAppraisal(selectedAppraisal._id, appraisalData);
      } else {
        await assignAppraisal(appraisalData);
      }
      message.success(`Appraisal ${selectedAppraisal ? "updated" : "assigned"} successfully!`);
      handleReload();
      handleCancel();
    } catch (error) {
      console.error("Error saving appraisal:", error);
      const errorMessage = error.message.includes("No employees found")
        ? "No employees found in the selected department."
        : error.message || "Failed to save appraisal.";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const kpiColumns = [
    {
      title: "KPI Title",
      dataIndex: "title",
      key: "title",
      render: (text, record, index) => (
        <Input
          placeholder="Enter KPI title (e.g., Sales Target)"
          value={record.title}
          onChange={(e) => handleKpiChange(index, "title", e.target.value)}
        />
      ),
    },
    {
      title: "Target",
      dataIndex: "target",
      key: "target",
      render: (text, record, index) => (
        <InputNumber
          placeholder="Enter target (e.g., 100)"
          min={1}
          value={record.target}
          onChange={(value) => handleKpiChange(index, "target", value)}
          style={{ width: "50%" }}
        />
      ),
    },
    {
      title: "Weight",
      dataIndex: "weightage",
      key: "weightage",
      render: (text, record, index) => (
        <InputNumber
          placeholder="Enter weightage (e.g., 50)"
          min={1}
          max={100}
          value={record.weightage}
          onChange={(value) => handleKpiChange(index, "weightage", value)}
          style={{ width: "50%" }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <Button danger onClick={() => removeKpi(index)}>
          <i data-feather="trash-2" className="feather-trash-2"></i>
        </Button>
      ),
    },
  ];

  const totalWeight = kpis.reduce((sum, kpi) => sum + Number(kpi.weightage || 0), 0);

  return (
    <div>
      <h4>{selectedAppraisal ? "Edit Appraisal" : "Assign Appraisal"}</h4>
      <div className="form-group">
        <label>Appraisal Name</label>
        <Input
          placeholder="Enter appraisal name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ width: "100%" }}
        />
      </div>
      <div className="form-group">
        <label>Department</label>
        <Select
          value={departmentId}
          onChange={setDepartmentId}
          options={departments.map((dept) => ({
            value: dept._id,
            label: dept.departmentName,
          }))}
          style={{ width: "100%" }}
          allowClear
          placeholder="Select a department"
        />
      </div>
      {selectedAppraisal && (
        <div className="form-group">
          <label>Employee</label>
          <Select
            mode="multiple"
            value={employeeIds}
            onChange={setEmployeeIds}
            options={employeeIds.map((id) => ({
              value: id,
              label: id,
            }))}
            style={{ width: "100%" }}
            allowClear
            placeholder="Select employees"
            disabled
          />
        </div>
      )}
      <div className="form-group">
        <label>Review Period</label>
        <Select
          value={periodId}
          onChange={setPeriodId}
          options={reviewPeriods.map((period) => ({
            value: period._id,
            label: period.name,
          }))}
          style={{ width: "100%" }}
          allowClear
          placeholder="Select a review period"
        />
      </div>
      <div className="form-group">
        <label>KPIs</label>
        <Table
          dataSource={kpis}
          columns={kpiColumns}
          rowKey={(record, index) => index}
          pagination={false}
          style={{ marginBottom: 16 }}
        />
        <Button onClick={addKpi} type="dashed" style={{ width: "100%" }}>
          Add KPI
        </Button>
        <p style={{ marginTop: 8 }}>
          Total Weightage: {totalWeight}%{" "}
          {totalWeight !== 100 && <span style={{ color: "red" }}>(Must be 100%)</span>}
        </p>
      </div>
      <div className="form-group">
        <label>Status</label>
        <Select
          value={status}
          onChange={setStatus}
          options={[
            { value: "Draft", label: "Draft" },
            { value: "InProgress", label: "In Progress" },
          ]}
          style={{ width: "100%" }}
          placeholder="Select status"
        />
      </div>
      <div className="form-group d-flex justify-content-end">
        <Button className="btn btn-cancel me-2" onClick={handleCancel}>
          Cancel
        </Button>
        <Button className="btn btn-submit" onClick={handleSubmit} loading={loading}>
          {selectedAppraisal ? "Update" : "Assign"}
        </Button>
      </div>
    </div>
  );
};

AssignAppraisal.propTypes = {
  selectedAppraisal: PropTypes.object,
  handleCancel: PropTypes.func.isRequired,
  handleReload: PropTypes.func.isRequired,
  reviewPeriods: PropTypes.array.isRequired,
  departments: PropTypes.array.isRequired,
};

export default AssignAppraisal;